import React, { createContext, useState, useContext, useCallback } from 'react';
import enTranslations from '../translations/en';
import esTranslations from '../translations/es';
import { track, ANALYTICS_EVENTS } from '../lib/analytics';

const bundledTranslations = { en: enTranslations, es: esTranslations };

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('synapsis-lang');
      if (saved && bundledTranslations[saved]) return saved;
      const browserLang = navigator.language?.slice(0, 2);
      if (bundledTranslations[browserLang]) return browserLang;
    }
    return 'en';
  });

  const [translations, setTranslations] = useState(
    () => bundledTranslations[language] || bundledTranslations.en
  );

  const changeLanguage = useCallback((newLanguage) => {
    const newTranslations = bundledTranslations[newLanguage];
    if (newTranslations) {
      // Tells us whether the English side is worth the upkeep, and whether US
      // visitors are landing on Spanish first.
      track(ANALYTICS_EVENTS.LANGUAGE_SWITCHED, { from: language, to: newLanguage });
    }
    setLanguage(newLanguage);
    if (newTranslations) {
      setTranslations(newTranslations);
      localStorage.setItem('synapsis-lang', newLanguage);
      document.documentElement.lang = newLanguage;
    }
  }, [language]);

  const t = useCallback((key, fallback) => {
    if (translations && translations[key]) {
      return translations[key];
    }
    return fallback || key;
  }, [translations]);

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
